import React, { useState, useEffect } from 'react';
import { fetchBackendApi } from '../../utils/apiUtils';
import { getAuthData, saveEvaluationHistory, getEvaluationHistory, deleteEvaluationHistoryItem, clearEvaluationHistory } from '../../utils/sessionStorage';

/**
 * QuantaEvaluationEngine - Advanced Clinical Entity Recognition and Evaluation
 * A/B testing platform for AWS Comprehend Medical vs Direct LLM extraction
 */
const EvaluationEngine = ({ 
  authData, 
  providerInfo,
  onEvaluationComplete
}) => {
  const [sourceContent, setSourceContent] = useState('');
  const [evaluationType, setEvaluationType] = useState('clinical_entities');
  const [extractionMode, setExtractionMode] = useState('both');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('input');
  const [evaluationHistory, setEvaluationHistory] = useState([]);
  
  // Load evaluation history on component mount
  useEffect(() => {
    const history = getEvaluationHistory();
    setEvaluationHistory(history);
  }, []);

  const handleEvaluate = async () => {
    if (!sourceContent.trim()) {
      setError('Please enter medical content to evaluate');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      const requestData = {
        evaluation_type: evaluationType,
        source_content: sourceContent,
        extraction_mode: extractionMode,
        options: {
          include_comparison: true,
          include_recommendations: true,
          preserve_verbatim: evaluationType === 'radiology_report'
        }
      };

      const endpoint = evaluationType === 'radiology_report' 
        ? '/api/provider/quanta/evaluate/radiology/'
        : '/api/provider/quanta/evaluate/';
      
      // Get auth data for the request
      const authData = getAuthData();
      if (!authData?.api_token) {
        throw new Error('Oops! Are you logged in? Please refresh the page and try again.');
      }
      
      const response = await fetchBackendApi(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authData.api_token}`
        },
        body: JSON.stringify(requestData)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      setResults(data);
      
      // Save to evaluation history with full content
      const historyEntry = {
        evaluation_id: data.evaluation_id,
        evaluation_type: evaluationType,
        extraction_mode: extractionMode,
        entity_count: data.results?.cer_to_llm?.entities?.length || 
                     data.results?.direct_llm?.entities?.length || 0,
        content_preview: sourceContent.substring(0, 100) + (sourceContent.length > 100 ? '...' : ''),
        full_content: sourceContent, // Store full content for loading later
        timestamp: data.timestamp
      };
      const updatedHistory = saveEvaluationHistory(historyEntry);
      setEvaluationHistory(updatedHistory);
      
      setActiveTab('results');
    } catch (err) {
      console.error('Evaluation error:', err);
      setError(err.message || 'Evaluation failed');
    } finally {
      setLoading(false);
    }
  };

  const renderEntityBadge = (entityType) => {
    const colorMap = {
      symptom: 'bg-danger',
      diagnosis: 'bg-primary',
      medication: 'bg-success',
      procedure: 'bg-info',
      laboratory: 'bg-warning',
      vital_sign: 'bg-secondary',
      allergy: 'bg-danger',
      anatomy: 'bg-dark',
      temporal: 'bg-light'
    };
    
    return (
      <span className={`badge ${colorMap[entityType] || 'bg-secondary'} me-1`}>
        {entityType.replace('_', ' ')}
      </span>
    );
  };

  const renderEntity = (entity, index) => (
    <div key={index} className="card mb-2">
      <div className="card-body p-3">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div>
            {renderEntityBadge(entity.entity_type)}
            {entity.negation && (
              <span className="badge bg-outline-danger me-1">NEGATED</span>
            )}
            {entity.uncertainty && (
              <span className="badge bg-outline-warning me-1">UNCERTAIN</span>
            )}
          </div>
          <small className="text-muted">
            {(entity.confidence_score * 100).toFixed(1)}%
          </small>
        </div>
        
        <div className="fw-bold mb-1">{entity.text}</div>
        
        {entity.normalized_text && entity.normalized_text !== entity.text && (
          <div className="text-muted mb-1">
            <strong>Normalized:</strong> {entity.normalized_text}
          </div>
        )}
        
        {entity.codes && Object.keys(entity.codes).length > 0 && (
          <div className="small text-muted mt-2">
            <strong>Codes:</strong>{' '}
            {Object.entries(entity.codes).map(([system, codes]) => (
              <span key={system} className="me-2">
                {system.toUpperCase()}: {Array.isArray(codes) ? codes.map(c => c.code || c).join(', ') : codes}
              </span>
            ))}
          </div>
        )}
        
        {entity.attributes && Object.keys(entity.attributes).length > 0 && (
          <div className="small text-muted mt-1">
            <strong>Attributes:</strong>{' '}
            {Object.entries(entity.attributes).map(([key, value]) => (
              <span key={key} className="me-2">{key}: {value}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderComparison = (comparison) => {
    if (!comparison) return null;

    const { entity_counts, overlap_metrics, performance, confidence } = comparison;
    const agreement = (overlap_metrics.agreement_score * 100).toFixed(1);
    const precision = ((overlap_metrics.precision || 0) * 100).toFixed(1);
    const recall = ((overlap_metrics.recall || 0) * 100).toFixed(1);
    const f1Score = ((overlap_metrics.f1_score || 0) * 100).toFixed(1);

    return (
      <div>
        <div className="row mb-4">
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <h3 className="text-primary">{entity_counts.path_a}</h3>
                <small className="text-muted">CER → LLM Entities</small>
              </div>
            </div>
          </div>
          
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <h3 className="text-success">{entity_counts.path_b}</h3>
                <small className="text-muted">Direct LLM Entities</small>
              </div>
            </div>
          </div>
          
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <h3 className="text-warning">{agreement}%</h3>
                <small className="text-muted">Agreement Score</small>
              </div>
            </div>
          </div>
          
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <h3 className="text-info">{overlap_metrics.common_entities}</h3>
                <small className="text-muted">Common Entities</small>
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Agreement Metrics */}
        <div className="row mb-3">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header">
                <h6 className="card-title mb-0">
                  <i className="fas fa-balance-scale me-2"></i>
                  Agreement Analysis
                </h6>
              </div>
              <div className="card-body">
                <div className="row text-center">
                  <div className="col-3">
                    <h5 className="text-primary">{precision}%</h5>
                    <small className="text-muted">Precision</small>
                  </div>
                  <div className="col-3">
                    <h5 className="text-success">{recall}%</h5>
                    <small className="text-muted">Recall</small>
                  </div>
                  <div className="col-3">
                    <h5 className="text-info">{f1Score}%</h5>
                    <small className="text-muted">F1 Score</small>
                  </div>
                  <div className="col-3">
                    <h5 className="text-warning">{agreement}%</h5>
                    <small className="text-muted">Jaccard Similarity</small>
                  </div>
                </div>
                <hr className="my-3" />
                <div className="row">
                  <div className="col-md-12">
                    <small className="text-muted">
                      <strong>Agreement Score:</strong> Measures entity overlap between methods (Jaccard similarity). 
                      <strong>Precision:</strong> % of CER→LLM entities found by Direct LLM. 
                      <strong>Recall:</strong> % of Direct LLM entities found by CER→LLM.
                      <strong>F1 Score:</strong> Harmonic mean of precision and recall.
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-md-6">
            <div className="card">
              <div className="card-header">
                <h6 className="card-title mb-0">
                  <i className="fas fa-clock me-2"></i>
                  Performance Metrics
                </h6>
              </div>
              <div className="card-body">
                <div className="d-flex justify-content-between mb-2">
                  <span>CER → LLM:</span>
                  <span>{performance.path_a_time_ms.toFixed(0)}ms</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span>Direct LLM:</span>
                  <span>{performance.path_b_time_ms.toFixed(0)}ms</span>
                </div>
                {performance.path_a_tokens && (
                  <div className="d-flex justify-content-between mb-2">
                    <span>CER → LLM Tokens:</span>
                    <span>{performance.path_a_tokens}</span>
                  </div>
                )}
                {performance.path_b_tokens && (
                  <div className="d-flex justify-content-between">
                    <span>Direct LLM Tokens:</span>
                    <span>{performance.path_b_tokens}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="card">
              <div className="card-header">
                <h6 className="card-title mb-0">
                  <i className="fas fa-chart-bar me-2"></i>
                  Confidence Scores
                </h6>
              </div>
              <div className="card-body">
                <div className="d-flex justify-content-between mb-2">
                  <span>CER → LLM:</span>
                  <span>{(confidence.path_a * 100).toFixed(1)}%</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Direct LLM:</span>
                  <span>{(confidence.path_b * 100).toFixed(1)}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {overlap_metrics.unique_to_path_a?.length > 0 && (
          <div className="card mb-3">
            <div className="card-header">
              <h6 className="card-title mb-0 text-primary">Only in CER → LLM</h6>
            </div>
            <div className="card-body">
              {overlap_metrics.unique_to_path_a.map((entity, idx) => (
                <span key={idx} className="badge bg-outline-secondary me-1 mb-1">
                  {entity}
                </span>
              ))}
            </div>
          </div>
        )}

        {overlap_metrics.unique_to_path_b?.length > 0 && (
          <div className="card mb-3">
            <div className="card-header">
              <h6 className="card-title mb-0 text-success">Only in Direct LLM</h6>
            </div>
            <div className="card-body">
              {overlap_metrics.unique_to_path_b.map((entity, idx) => (
                <span key={idx} className="badge bg-outline-secondary me-1 mb-1">
                  {entity}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderRadiologyResults = (results) => {
    if (!results.results) return null;

    const { scan_metadata, findings, assessment, confidence_score } = results.results;

    return (
      <div>
        {/* Scan Metadata */}
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="card-title mb-0">Scan Metadata</h5>
          </div>
          <div className="card-body">
            {scan_metadata?.patient && (
              <div className="mb-3">
                <h6 className="fw-bold">Patient Information</h6>
                <div className="row">
                  {Object.entries(scan_metadata.patient).map(([key, value]) => (
                    value && (
                      <div key={key} className="col-md-6 mb-1">
                        <small><strong>{key}:</strong> {value}</small>
                      </div>
                    )
                  ))}
                </div>
              </div>
            )}
            
            {scan_metadata?.scan && (
              <div>
                <h6 className="fw-bold">Scan Details</h6>
                <div className="row">
                  {Object.entries(scan_metadata.scan).map(([key, value]) => (
                    value && (
                      <div key={key} className="col-md-6 mb-1">
                        <small><strong>{key}:</strong> {value}</small>
                      </div>
                    )
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Findings */}
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="card-title mb-0">Medical Findings</h5>
          </div>
          <div className="card-body">
            {findings?.abnormal_findings_verbatim?.length > 0 && (
              <div className="mb-3">
                <h6 className="fw-bold text-danger">Abnormal Findings (Verbatim)</h6>
                {findings.abnormal_findings_verbatim.map((finding, idx) => (
                  <div key={idx} className="alert alert-danger border-start border-danger border-4 mb-2">
                    <small>{finding}</small>
                  </div>
                ))}
              </div>
            )}

            {findings?.normal_findings?.length > 0 && (
              <div className="mb-3">
                <h6 className="fw-bold text-success">Normal Findings</h6>
                {findings.normal_findings.map((finding, idx) => (
                  <div key={idx} className="alert alert-success border-start border-success border-4 mb-2">
                    <small>{finding}</small>
                  </div>
                ))}
              </div>
            )}

            {findings?.measurements?.length > 0 && (
              <div>
                <h6 className="fw-bold">Measurements</h6>
                {findings.measurements.map((measurement, idx) => (
                  <span key={idx} className="badge bg-outline-secondary me-2 mb-1">
                    {measurement}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Assessment */}
        <div className="card">
          <div className="card-header">
            <h5 className="card-title mb-0 d-flex align-items-center">
              Overall Assessment
              <span className={`badge ms-2 ${
                assessment?.category === 'Normal scan' ? 'bg-success' :
                assessment?.category === 'Abnormal scan' ? 'bg-danger' :
                'bg-warning'
              }`}>
                {assessment?.category}
              </span>
              <span className="badge bg-secondary ms-2">
                {confidence_score}% Confidence
              </span>
            </h5>
          </div>
          <div className="card-body">
            {assessment?.summary && (
              <p className="mb-3">{assessment.summary}</p>
            )}
            {assessment?.recommendations?.length > 0 && (
              <div>
                <h6 className="fw-bold">Recommendations</h6>
                <ul className="list-unstyled">
                  {assessment.recommendations.map((rec, idx) => (
                    <li key={idx} className="mb-1">
                      <i className="fas fa-chevron-right me-2 text-muted"></i>
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="ankr-cases-container">
      {/* 
        <div className="col-12"> */}
          <div className="ankr-cases-header mb-2">
            <div className="ankr-header-left">
            <h3>Quanta</h3>
            <p>Helps your team analyze clinic notes, radiology reports and more. Like magic!</p>
            </div>
            <h3>🧠</h3>
            {/* <i className='fas fa-brain fa-2x text-danger'></i> */}
            {/* <span className="badge bg-primary ms-2">A/B Testing</span> */}
          </div>
          
          {/* Navigation Tabs */}
          <div className="row m-0 p-3">
          <ul className="nav nav-pills mb-4" role="tablist">
            <li className="nav-item" role="presentation">
              <button 
                className={`nav-link ${activeTab === 'input' ? 'active' : ''}`}
                onClick={() => setActiveTab('input')}
                type="button"
                role="tab"
              >
                Input & Configuration
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button 
                className={`nav-link ${activeTab === 'results' ? 'active' : ''} ${!results ? 'disabled' : ''}`}
                onClick={() => results && setActiveTab('results')}
                type="button"
                role="tab"
                disabled={!results}
              >
                Results
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button 
                className={`nav-link ${activeTab === 'comparison' ? 'active' : ''} ${!results?.results?.comparison ? 'disabled' : ''}`}
                onClick={() => results?.results?.comparison && setActiveTab('comparison')}
                type="button"
                role="tab"
                disabled={!results?.results?.comparison}
              >
                Comparison
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button 
                className={`nav-link ${activeTab === 'history' ? 'active' : ''}`}
                onClick={() => setActiveTab('history')}
                type="button"
                role="tab"
              >
                History
                {evaluationHistory.length > 0 && (
                  <span className="badge bg-secondary mx-1 mt-0">{evaluationHistory.length}</span>
                )}
              </button>
            </li>
          </ul>

          {/* Tab Content */}
          <div className="tab-content">
            {/* Input Tab */}
            {activeTab === 'input' && (
              <div className="tab-pane fade show active">
                <div className="card">
                  <div className="card-header">
                    <h4 className="card-title mb-0">Evaluation Configuration</h4>
                  </div>
                  <div className="card-body">
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label className="form-label">Evaluation Type</label>
                        <select 
                          className="form-select" 
                          value={evaluationType} 
                          onChange={(e) => setEvaluationType(e.target.value)}
                        >
                          <option value="clinical_entities">Clinical Entity Recognition</option>
                          <option value="radiology_report">Radiology Report Analysis</option>
                        </select>
                      </div>

                      {evaluationType === 'clinical_entities' && (
                        <div className="col-md-6">
                          <label className="form-label">Extraction Mode</label>
                          <select 
                            className="form-select" 
                            value={extractionMode} 
                            onChange={(e) => setExtractionMode(e.target.value)}
                          >
                            <option value="both">A/B Comparison (Both Methods)</option>
                            <option value="cer_to_llm">Sol Quanta CER → LLM</option>
                            <option value="direct_llm">Sol Quanta LLM Only</option>
                          </select>
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        Medical Content to Evaluate
                        <small className="text-muted ms-2">
                          ({evaluationType === 'radiology_report' ? 'CT/MRI report' : 'Clinical notes, transcripts, or medical text'})
                        </small>
                      </label>
                      <textarea
                        className="form-control font-monospace"
                        value={sourceContent}
                        onChange={(e) => setSourceContent(e.target.value)}
                        placeholder={
                          evaluationType === 'radiology_report'
                            ? 'Paste CT or MRI report here...'
                            : 'Paste clinical notes, transcription, or medical text here...'
                        }
                        rows={12}
                      />
                    </div>

                    {error && (
                      <div className="alert alert-danger d-flex align-items-center" role="alert">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        {error}
                      </div>
                    )}

                    <button 
                      className="btn btn-primary btn-lg w-100"
                      onClick={handleEvaluate} 
                      disabled={loading || !sourceContent.trim()}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Evaluating...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-bolt me-2"></i>
                          Run Evaluation
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Results Tab */}
            {activeTab === 'results' && (
              <div className="tab-pane fade show active">
                {!results ? (
                  <div className="text-center py-5">
                    <i className="fas fa-flask fs-1 text-muted mb-3"></i>
                    <p className="text-muted">No evaluation results yet</p>
                    <small className="text-muted">Run an evaluation to see results here</small>
                  </div>
                ) : (
                  evaluationType === 'radiology_report' ? 
                    renderRadiologyResults(results) : 
                    (
                    <div>
                      {results.results.cer_to_llm && (
                        <div className="card mb-4">
                          <div className="card-header">
                            <h5 className="card-title mb-0 d-flex align-items-center">
                              <span className="badge bg-primary rounded-pill me-2">CER → LLM</span>
                              Results
                              <span className="badge bg-secondary ms-2">
                                {results.results.cer_to_llm.entities.length} entities
                              </span>
                            </h5>
                          </div>
                          <div className="card-body" style={{maxHeight: '400px', overflowY: 'auto'}}>
                            {results.results.cer_to_llm.entities.map(renderEntity)}
                          </div>
                        </div>
                      )}

                      {results.results.direct_llm && (
                        <div className="card mb-4">
                          <div className="card-header">
                            <h5 className="card-title mb-0 d-flex align-items-center">
                              <span className="badge bg-success rounded-pill me-2">Direct LLM</span>
                              Results
                              <span className="badge bg-secondary ms-2">
                                {results.results.direct_llm.entities.length} entities
                              </span>
                            </h5>
                          </div>
                          <div className="card-body" style={{maxHeight: '400px', overflowY: 'auto'}}>
                            {results.results.direct_llm.entities.map(renderEntity)}
                          </div>
                        </div>
                      )}

                      {results.recommendations && results.recommendations.length > 0 && (
                        <div className="alert alert-info">
                          <h6 className="alert-heading">
                            <i className="fas fa-lightbulb me-2"></i>
                            Recommendations
                          </h6>
                          <ul className="mb-0">
                            {results.recommendations.map((rec, idx) => (
                              <li key={idx}>{rec}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    )
                )}
              </div>
            )}

            {/* Comparison Tab */}
            {activeTab === 'comparison' && results?.results?.comparison && (
              <div className="tab-pane fade show active">
                {renderComparison(results.results.comparison)}
              </div>
            )}
            
            {/* History Tab */}
            {activeTab === 'history' && (
              <div className="tab-pane fade show active">
                <div className="card">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h4 className="card-title mb-0">Recent Evaluations</h4>
                    {evaluationHistory.length > 0 && (
                      <button 
                        className="btn btn-sm btn-danger"
                        onClick={() => {
                          if (window.confirm('Are you sure you want to delete all evaluation history?')) {
                            clearEvaluationHistory();
                            setEvaluationHistory([]);
                          }
                        }}
                      >
                        <i className="fas fa-trash-alt me-1"></i>
                        Delete All
                      </button>
                    )}
                  </div>
                  <div className="card-body">
                    {evaluationHistory.length === 0 ? (
                      <p className="text-muted text-center py-4">No evaluation history yet</p>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-hover">
                          <thead>
                            <tr>
                              <th>Time</th>
                              <th>Type</th>
                              <th>Mode</th>
                              <th>Entities</th>
                              <th>Preview</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {evaluationHistory.map((item, index) => (
                              <tr key={item.id || index}>
                                <td>
                                  <small>{new Date(item.timestamp).toLocaleString()}</small>
                                </td>
                                <td>
                                  <span className={`badge ${item.evaluation_type === 'radiology_report' ? 'bg-info' : 'bg-primary'}`}>
                                    {item.evaluation_type === 'radiology_report' ? 'Radiology' : 'Clinical'}
                                  </span>
                                </td>
                                <td>
                                  <small>{item.extraction_mode}</small>
                                </td>
                                <td>
                                  <span className="badge bg-secondary">{item.entity_count}</span>
                                </td>
                                <td>
                                  <small className="text-muted" title={item.full_content ? "Full content saved" : "Preview only"}>
                                    {item.content_preview}
                                    {item.full_content && (
                                      <i className="fas fa-check-circle text-success ms-1" title="Full content available"></i>
                                    )}
                                  </small>
                                </td>
                                <td>
                                  <div className="btn-group" role="group">
                                    <button 
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() => {
                                        // Load full content if available, otherwise use preview
                                        setSourceContent(item.full_content || item.content_preview.replace('...', ''));
                                        setEvaluationType(item.evaluation_type);
                                        setExtractionMode(item.extraction_mode);
                                        setActiveTab('input');
                                      }}
                                      title="Load this evaluation"
                                    >
                                      <i className="fas fa-upload me-1"></i>
                                      Load
                                    </button>
                                    <button 
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => {
                                        if (window.confirm('Delete this evaluation from history?')) {
                                          const updatedHistory = deleteEvaluationHistoryItem(item.id);
                                          setEvaluationHistory(updatedHistory);
                                        }
                                      }}
                                      title="Delete this entry"
                                    >
                                      <i className="fas fa-trash"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
    </div>
    // </div>
  );
};

export default EvaluationEngine;